<div
  class="message"
  data-testid="chat-message"
  [attr.data-message-id]="message.messageId"
  [attr.data-role]="message.role"
  [attr.data-streaming]="message.streaming || false"
  [attr.data-failed]="message.failed || false"
  [attr.data-model]="message.metadata?.costAnalysis?.model || ''"
  [attr.data-total-tokens]="message.metadata?.costAnalysis?.totalTokens || 0"
  [attr.data-cost-usd]="message.metadata?.costAnalysis?.estimatedTotalCostUsd || 0"
  [class.user-message]="isUser"
  [class.assistant-message]="!isUser"
  [class.queued-message]="message.queued"
  [class.failed-message]="message.failed"
>
  @if (showAvatar) {
    <div class="avatar">
      @if (isUser) {
        @if (userPhotoUrl$ | async; as userPhoto) {
          @if (userPhoto?.photoURL) {
            <img [src]="userPhoto.photoURL" [alt]="'User avatar'" />
          } @else {
            <mat-icon>{{ avatar }}</mat-icon>
          }
        } @else {
          <mat-icon>{{ avatar }}</mat-icon>
        }
      } @else {
        <img src="assets/images/permitproof-logo.svg" alt="PermitProof Assistant" />
      }
    </div>
  }

  <div class="message-wrapper">
    <div class="message-header">
      @if (message.modality === 'voice') {
        <div class="modality-badge voice-badge" matTooltip="Voice message">
          <mat-icon>mic</mat-icon>
          <span>Voice</span>
        </div>
      }
      <div class="message-timestamp">{{ formatMessageTimestamp(message.timestamp) }}</div>
    </div>
    <div class="message-content">
      <!-- Attached images (Issue #1746). Shown above the text so a restored conversation reads the
           way it was written: the picture first, then the question about it. -->
      @if (message.attachments && message.attachments.length > 0) {
        <app-attachment-thumbnails [attachments]="message.attachments"></app-attachment-thumbnails>
      }

      <!-- Timeline: Chronological thinking + tool calls (Issue #270) -->
      @if (message.metadata && message.metadata.timeline && message.metadata.timeline.length > 0) {
        <div class="timeline-section">
          @for (event of message.metadata.timeline; track $index) {
            <!-- Thinking event -->
            @if (event.type === 'thinking') {
              <div class="thinking-section">
                <mat-expansion-panel
                  class="thinking-panel"
                  [expanded]="isPanelExpanded($index, event)"
                  (opened)="onPanelToggle($index, true)"
                  (closed)="onPanelToggle($index, false)"
                >
                  <mat-expansion-panel-header>
                    <mat-panel-title>
                      <mat-icon>psychology</mat-icon>
                      <span class="thinking-label">{{ getThinkingHeaderLabel(event) }}</span>
                      @if (getThinkingDuration(event, elapsedTimer$ | async); as duration) {
                        <span class="duration">{{ duration }}</span>
                      }
                    </mat-panel-title>
                  </mat-expansion-panel-header>
                  <div class="thinking-content" [innerHTML]="renderMarkdown(event.content || '', $index)"></div>
                  @if (isThinkingInProgress(event)) {
                    <div class="thinking-streaming-indicator">
                      <span class="dot"></span>
                      <span class="dot"></span>
                      <span class="dot"></span>
                    </div>
                  }
                </mat-expansion-panel>
              </div>
            }
            <!-- Tool call event -->
            @if (event.type === 'tool_call' && event.toolCall) {
              <div class="tool-calls-section">
                <mat-expansion-panel
                  class="tool-call-panel"
                  [class.tool-error]="
                    (event.toolCall.error || event.toolCall.status === 'FAILED') &&
                    event.toolCall.status !== 'NOT_FOUND'
                  "
                  [class.tool-not-found]="event.toolCall.status === 'NOT_FOUND'"
                >
                  <mat-expansion-panel-header>
                    <mat-panel-title>
                      <mat-icon class="tool-icon">{{ getToolIcon(event.toolCall.toolName) }}</mat-icon>
                      <span class="tool-name"
                        >{{ formatToolName(event.toolCall.toolName) }}
                        @if (getToolContext(event.toolCall)) {
                          <span class="tool-context"> — {{ getToolContext(event.toolCall) }}</span>
                        }
                      </span>
                      <span class="status-badge" [class]="getStatusClass(event.toolCall)">
                        {{ getStatusLabel(event.toolCall) }}
                      </span>
                      <span class="duration">{{
                        isToolCallInProgress(event.toolCall)
                          ? formatElapsed(event, elapsedTimer$ | async)
                          : formatDuration(event.toolCall.durationMs)
                      }}</span>
                    </mat-panel-title>
                  </mat-expansion-panel-header>
                  <div class="tool-details">
                    <!-- Operation name + invocation ID -->
                    <div class="detail-section metadata">
                      <h4>Operation</h4>
                      <span class="field-label">Name</span>
                      <code class="operation-name" aria-label="Operation name">{{ event.toolCall.toolName }}</code>
                      <span class="field-label">Invocation ID</span>
                      <code class="operation-id" aria-label="Invocation ID">{{ event.toolCall.operationId }}</code>
                    </div>
                    <!-- Parameters -->
                    <div class="detail-section">
                      <h4>Parameters</h4>
                      <pre class="code-block" [innerHTML]="highlightParams(event.toolCall)"></pre>
                    </div>
                    <!-- Result (truncated if large) -->
                    @if (event.toolCall.result && !event.toolCall.error) {
                      <div class="detail-section">
                        <h4>Response</h4>
                        <pre class="code-block" [innerHTML]="getDisplayResult(event.toolCall)"></pre>
                        @if (isResultTruncated(event.toolCall)) {
                          <button mat-button (click)="toggleFullResult(event.toolCall)">
                            {{ isResultExpanded(event.toolCall) ? 'Show less' : 'Show full response' }}
                          </button>
                        }
                      </div>
                    }
                    <!-- Error -->
                    @if (event.toolCall.error) {
                      <div class="detail-section error">
                        <h4>Error</h4>
                        <pre class="error-block">{{ event.toolCall.error }}</pre>
                      </div>
                    }
                  </div>
                </mat-expansion-panel>
              </div>
            }
          }
        </div>
      }

      @if (message.metadata?.reasoningUnavailable) {
        <p class="reasoning-unavailable-notice">The model's reasoning was not returned for this response.</p>
      }

      <!-- Selected document quotes attached to message (Issue #1745) -->
      @if (message.selectedTexts && message.selectedTexts.length > 0) {
        <div class="message-quotes-section">
          @for (quote of message.selectedTexts; track quote.id || quote.selectedText) {
            <button
              type="button"
              class="quote-chip"
              (click)="navigateToQuote(quote)"
              [matTooltip]="'Open page ' + quote.pageNumber + ' and focus selection'"
            >
              <mat-icon class="quote-icon">format_quote</mat-icon>
              <span class="quote-text">"{{ quote.excerpt || quote.selectedText }}"</span>
              <span class="quote-source">
                {{ quote.fileName ? quote.fileName + ' · ' : '' }}P.{{ quote.pageNumber
                }}{{ quote.regionPath ? ' · ' + quote.regionPath : '' }}
              </span>
              <mat-icon class="quote-open-icon">open_in_new</mat-icon>
            </button>
          }
        </div>
      }

      <!-- Main message text or typing indicator -->
      <div class="markdown-renderer-container">
        @if (renderedContent) {
          <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -- click handler delegates to links within rendered markdown; container itself is not interactive -->
          <div
            class="message-text"
            [class.streaming-text]="isStreaming"
            (click)="onLinkClick($event)"
            [innerHTML]="renderedContent"
          ></div>
        }
      </div>

      <!-- Thinking Placeholder — only while actively streaming (Issue #966) -->
      @if (!isUser && !message.content && !message.metadata?.timeline?.length && message.streaming) {
        <div class="timeline-section">
          <div class="thinking-section">
            <mat-expansion-panel class="thinking-panel" [expanded]="true" [hideToggle]="true" [disabled]="true">
              <mat-expansion-panel-header>
                <mat-panel-title>
                  <mat-icon>psychology</mat-icon>
                  <span>Thinking...</span>
                </mat-panel-title>
              </mat-expansion-panel-header>
              <div class="thinking-content">
                <div class="typing-dots">
                  <span class="dot"></span>
                  <span class="dot"></span>
                  <span class="dot"></span>
                </div>
              </div>
            </mat-expansion-panel>
          </div>
        </div>
      }

      @if (!isUser && message.failed) {
        <div class="failure-actions">
          @if (message.errorAction === 'OPEN_BILLING') {
            <button mat-flat-button class="billing-button" (click)="openErrorAction()" aria-label="Open Billing">
              <mat-icon>account_balance_wallet</mat-icon>
              Open Billing
            </button>
          }
          @if (message.retryable) {
            <button
              mat-stroked-button
              class="retry-button"
              [disabled]="retryDisabled"
              (click)="retry()"
              aria-label="Retry this response"
            >
              <mat-icon>refresh</mat-icon>
              Retry
            </button>
          }
          <button mat-button class="report-problem-button" (click)="reportError()" aria-label="Report this problem">
            <mat-icon>bug_report</mat-icon>
            Report this problem
          </button>
        </div>
      }

      <!-- Message actions -->
      <div class="message-actions">
        <button mat-icon-button (click)="copyContent()" matTooltip="Copy">
          <mat-icon>content_copy</mat-icon>
        </button>
      </div>
    </div>

    <!-- Typing subscript — only while actively streaming (Issue #966). Issue #2989: shows the
         server's progress note while the turn is otherwise silent. -->
    @if (!isUser && message.streaming && (message.progress || !message.content)) {
      <div class="typing-subscript" role="status" aria-live="polite">{{ message.progress || 'Thinking...' }}</div>
    }

    @if (!isUser && message.failed) {
      <div class="failed-subscript" role="status">
        <mat-icon>error_outline</mat-icon>
        <span>Response failed</span>
        @if (message.errorCorrelationId) {
          <span class="error-reference">Reference: {{ message.errorCorrelationId }}</span>
        }
      </div>
    }

    <!-- Queued indicator - outside bubble (for user messages) -->
    @if (isUser && message.queued) {
      <div class="queued-subscript">Queued</div>
    }
  </div>
</div>
